import { Response200Type as MarkersOfHarmPopupApiType } from 'src/api_openapi/generated/openapi_website_cms_createMarkersOfHarmPopup';
import { Response200Type as MarkersOfHarmTriggerListApiType } from 'src/api_openapi/generated/openapi_moh_service_getTriggers';
import { Response200Type as MarkersOfHarmTriggerTemplateListApiType } from 'src/api_openapi/generated/openapi_moh_service_getTriggerTemplates';
import { z } from 'zod';
import {
    MarkersOfHarmTriggerType,
    MarkersOfHarmPopupType,
    PopupButtonActionType,
    PopupButtonFormType,
    PopupTriggerTemplateType,
    PopupTriggerActionType,
    PopupTriggerSettingsFormType,
    PopupTriggerParamType,
    PopupTriggerParamValueType,
    PopupRiskLevelType,
} from './MarkersOfHarmPopupTab.types';

const popupButtonActionSchema = z.enum(['sign-out', 'redirect-to-page', 'account-suspension', 'none']);

const popupRiskLevelSchema = z.enum(['low', 'medium', 'high']);

const parseButtonAction = (action: unknown): PopupButtonActionType => {
    const parsed = popupButtonActionSchema.safeParse(action);
    return parsed.success ? parsed.data : 'none';
};

const parseRiskLevel = (riskLevel: unknown): PopupRiskLevelType => {
    const parsed = popupRiskLevelSchema.safeParse(riskLevel);
    return parsed.success ? parsed.data : 'low';
};

const triggerActionSchema = z.object({
    type: z.literal('automation_action'),
    name: z.string(),
});

const triggerParamValueSchema = z.union([
    z.number(),
    z.string(),
    z.boolean(),
    z.object({
        type: z.literal('rolling'),
        value: z.string(),
    }),
    z.object({
        type: z.literal('time-range'),
        start: z.string(),
        end: z.string(),
    }),
]);

const parseTriggerActions = (actions: unknown[]): PopupTriggerActionType[] => {
    return actions.flatMap((action) => {
        const parsed = triggerActionSchema.safeParse(action);
        return parsed.success ? [parsed.data] : [];
    });
};
const finiteNumberSchema = z.number().finite();

const getDurationHoursFromParamValue = (value: unknown): number | null => {
    if (typeof value === 'number' && finiteNumberSchema.safeParse(value).success) {
        return Math.trunc(value);
    }

    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const objectValue = Object.fromEntries(Object.entries(value));
    if (typeof objectValue.hours === 'number' && finiteNumberSchema.safeParse(objectValue.hours).success) {
        return Math.trunc(objectValue.hours);
    }

    return null;
};

const mapButtonFromApi = (button: {
    action: string;
    redirect_url?: null | undefined | string;
    text: string;
}): PopupButtonFormType => ({
    text: button.text,
    action: parseButtonAction(button.action),
    redirectUrl: button.redirect_url ?? undefined,
});

const mapButtonToApi = (
    button: PopupButtonFormType
): {
    action: string;
    redirect_url?: string;
    text: string;
} => {
    const payload: { action: string; redirect_url?: string; text: string } = {
        text: button.text,
        action: button.action,
    };

    if (button.redirectUrl !== undefined && button.redirectUrl.trim() !== '') {
        payload.redirect_url = button.redirectUrl.trim();
    }

    return payload;
};

const triggerTemplateParamSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    type: z.string(),
    allowedKinds: z.array(z.string()).optional(),
});

const mapTriggerTemplateParamFromApi = (
    param: MarkersOfHarmTriggerTemplateListApiType[number]['params'][number]
): PopupTriggerParamType => {
    const parsed = triggerTemplateParamSchema.safeParse(param);

    if (!parsed.success) {
        throw new Error(`Invalid trigger template param "${JSON.stringify(param)}".`);
    }

    return parsed.data;
};

export const mapTriggerTemplateFromApi = (
    triggerTemplate: MarkersOfHarmTriggerTemplateListApiType[number]
): PopupTriggerTemplateType => ({
    id: triggerTemplate.id,
    displayName: triggerTemplate.displayName,
    description: triggerTemplate.description,
    params: triggerTemplate.params.map(mapTriggerTemplateParamFromApi),
});

export const mapTriggerFromApi = (trigger: MarkersOfHarmTriggerListApiType[number]): MarkersOfHarmTriggerType => {
    const params: Record<string, unknown> = {};

    trigger.params.forEach((param) => {
        if (param.value !== undefined) {
            params[param.name] = param.value;
            return;
        }

        if (
            param.type === 'duration' &&
            'hours' in param &&
            typeof param.hours === 'number' &&
            finiteNumberSchema.safeParse(param.hours).success
        ) {
            params[param.name] = Math.trunc(param.hours);
        }
    });

    return {
        id: trigger.id,
        templateId: trigger.templateId,
        name: trigger.name,
        params,
        actions: parseTriggerActions(trigger.actions),
        riskLevel: parseRiskLevel(trigger.riskLevel),
        enabled: trigger.enabled,
    };
};

const buildCommonTriggerPayload = (
    settings: PopupTriggerSettingsFormType,
    automationName: string,
    riskLevel: PopupRiskLevelType,
    enabled: boolean
): {
    templateId: string;
    params: Record<string, PopupTriggerParamValueType>;
    actions: PopupTriggerActionType[];
    riskLevel: PopupRiskLevelType;
    enabled: boolean;
} => {
    return {
        templateId: settings.templateId,
        params: settings.params,
        actions: [{ type: 'automation_action', name: automationName }],
        riskLevel,
        enabled,
    };
};

export const mapTriggerCreatePayloadFromForm = (
    settings: PopupTriggerSettingsFormType,
    automationName: string,
    riskLevel: PopupRiskLevelType,
    enabled = true
): {
    templateId: string;
    params: Record<string, PopupTriggerParamValueType>;
    actions: PopupTriggerActionType[];
    riskLevel: PopupRiskLevelType;
    enabled: boolean;
} => {
    return buildCommonTriggerPayload(settings, automationName, riskLevel, enabled);
};

export const mapTriggerUpdatePayloadFromForm = (
    settings: PopupTriggerSettingsFormType,
    automationName: string,
    riskLevel: PopupRiskLevelType,
    enabled: boolean
): {
    params: Record<string, PopupTriggerParamValueType>;
    actions: PopupTriggerActionType[];
    riskLevel: PopupRiskLevelType;
    enabled: boolean;
} => {
    const payload = buildCommonTriggerPayload(settings, automationName, riskLevel, enabled);
    return {
        params: payload.params,
        actions: payload.actions,
        riskLevel: payload.riskLevel,
        enabled: payload.enabled,
    };
};

export const mapTriggerToFormValues = (
    trigger: MarkersOfHarmTriggerType,
    triggerTemplate: PopupTriggerTemplateType | null
): PopupTriggerSettingsFormType | null => {
    if (triggerTemplate === null) {
        return null;
    }

    const params: Record<string, PopupTriggerParamValueType> = {};

    triggerTemplate.params.forEach((param) => {
        const paramValue = trigger.params[param.name];
        if (paramValue !== undefined) {
            const parsedValue = triggerParamValueSchema.safeParse(paramValue);
            if (parsedValue.success) {
                params[param.name] = parsedValue.data;
                return;
            }

            if (param.type === 'duration') {
                const hoursValue = getDurationHoursFromParamValue(paramValue);
                if (hoursValue !== null) {
                    params[param.name] = hoursValue;
                }
            }
        }
    });

    return {
        templateId: trigger.templateId,
        params,
    };
};

export const mapPopupFromApi = (
    popup: MarkersOfHarmPopupApiType,
    triggerIdFromNameMap?: Map<string, number>
): MarkersOfHarmPopupType => {
    const popupWithTriggerId = Object.fromEntries(Object.entries(popup));
    const triggerIdValue = popupWithTriggerId.trigger_id;
    const triggerNameValue = popupWithTriggerId.trigger_name;
    const mappedTriggerId =
        (typeof triggerIdValue === 'number' && triggerIdValue > 0 ? triggerIdValue : undefined) ??
        (typeof triggerNameValue === 'string' ? triggerIdFromNameMap?.get(triggerNameValue) : undefined);

    if (mappedTriggerId === undefined) {
        throw new Error(`Markers of Harm popup "${popup.id}" is missing trigger_id mapping.`);
    }

    return {
        id: String(popup.id),
        title: popup.title,
        bodyText: popup.description,
        primaryButton: mapButtonFromApi(popup.primary_button),
        secondaryButton:
            popup.secondary_button === null || popup.secondary_button === undefined
                ? undefined
                : mapButtonFromApi(popup.secondary_button),
        triggerId: mappedTriggerId,
        active: popup.active,
        activationDate: popup.activation_date ?? undefined,
        deactivationDate: popup.deactivation_date ?? undefined,
        createdAt: popup.created_at,
    };
};

export const mapPopupPayloadToApi = (
    payload: Omit<MarkersOfHarmPopupType, 'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger'>
): {
    active: boolean;
    description: string;
    primary_button: { action: string; redirect_url?: string; text: string };
    secondary_button?: { action: string; redirect_url?: string; text: string };
    trigger_id: number;
    title: string;
} => ({
    title: payload.title,
    description: payload.bodyText,
    primary_button: mapButtonToApi(payload.primaryButton),
    secondary_button: payload.secondaryButton === undefined ? undefined : mapButtonToApi(payload.secondaryButton),
    trigger_id: payload.triggerId,
    active: payload.active,
});
